import type { DerivedSheet, StrikeView } from "../engine";
import { signed } from "../engine";
import { damageLabel, parseDamage } from "./enrich";
import { RollButton } from "./roll-button";
import { useSheetActions } from "./sheet-context";
import type { StatDetail } from "./stat-detail";
import { strikeDamageDetail, strikeDetail } from "./stat-detail";

interface Props {
  sheet: DerivedSheet;
  onStat: (detail: StatDetail, dc?: number | null) => void;
}

/**
 * The Strikes tab (issue #14): one card per Strike with the three Multiple Attack Penalty
 * variants as Roll buttons (tap to roll, hold to enter a physical die, #19), the damage with its
 * critical formula, traits, range and readiness; alternate usages under their weapon. Tap the
 * breakdown for the modifiers; the attack takes an Override.
 */
export function Strikes({ sheet, onStat }: Props) {
  const ctx = { sheet };
  const strikes = sheet.strikes.filter((s) => s.visible);
  return (
    <div class="strikes">
      {strikes.length === 0 && <p class="muted">No Strikes: the basic unarmed attack appears once the Character has a class.</p>}
      {strikes.map((st) => (
        <StrikeCard key={st.overrideKey} strike={st} ctx={ctx} onStat={onStat} />
      ))}
    </div>
  );
}

/** Foundry's roll formula in words: "{1d4[bludgeoning]}" reads "1d4 bludgeoning". */
export function readableDamage(formula: string | null): string {
  if (!formula) return "—";
  const label = damageLabel(parseDamage(formula));
  return label || formula;
}

function StrikeCard({ strike: st, ctx, onStat, alt }: { strike: StrikeView; ctx: { sheet: DerivedSheet }; onStat: Props["onStat"]; alt?: boolean }) {
  const { openEntry } = useSheetActions();
  const damage = st.damage;
  return (
    <section class={`card strike${st.ready ? "" : " not-ready"}${alt ? " alt" : ""}`}>
      <div class="strike-head">
        <h3>
          {alt ? <span class="muted">↳ </span> : null}
          {st.source ? <button type="button" class="linkish" onClick={() => openEntry(st.source!)}>{st.label}</button> : st.label}
          {st.thrown ? <span class="tag">thrown {st.rangeIncrement} ft</span> : st.ranged ? <span class="tag">range {st.rangeIncrement} ft</span> : null}
          {!st.ready && <span class="tag warn">not ready</span>}
          {st.override && <span class="tag">Override</span>}
        </h3>
        <span class="muted strike-meta">
          {st.sourceName && st.sourceName !== st.label ? `${st.sourceName} · ` : ""}{st.category}{st.group ? ` · ${st.group}` : ""}{st.handsHeld > 0 ? ` · ${st.handsHeld}h` : ""}
          {st.ammunition ? ` · ammunition: ${st.ammunition.selected ? (st.ammunition.compatible.find((a) => a.id === st.ammunition!.selected)?.name ?? "selected") : "none selected"}` : ""}
        </span>
      </div>
      <div class="strike-rolls">
        {st.variants.map((v, i) => (
          <RollButton key={i} class="roll-strike" request={{ kind: "strike", strikeKey: st.overrideKey, label: st.label, map: i as 0 | 1 | 2, modifier: v.total }} title={`${i === 0 ? "First attack" : `Multiple Attack Penalty ${v.penalty}`}: tap to roll, hold to enter a physical die`} disabled={!st.ready}>
            <span class="roll-label">{i === 0 ? "Strike" : `MAP ${i}`}</span>
            <span class="roll-mod">{signed(v.total)}</span>
          </RollButton>
        ))}
        <button type="button" class="roll roll-breakdown" onClick={() => onStat(strikeDetail(st, ctx))}>Breakdown{st.override ? ` (engine ${signed(st.derived)})` : ""}</button>
      </div>
      {damage ? (
        <div class="strike-rolls">
          <RollButton class="roll-strike-damage" request={{ kind: "strike-damage", strikeKey: st.overrideKey, label: `${st.label} damage`, formula: damage.formula.success ?? "", critical: damage.formula.criticalSuccess }} disabled={!st.ready}>
            <span class="roll-label">Damage</span>
            <span class="roll-mod">{readableDamage(damage.formula.success)}</span>
          </RollButton>
          {damage.formula.criticalSuccess && (
            <RollButton class="roll-strike-damage" request={{ kind: "strike-damage", strikeKey: st.overrideKey, label: `${st.label} damage`, formula: damage.formula.success ?? "", critical: damage.formula.criticalSuccess, asCritical: true }} disabled={!st.ready}>
              <span class="roll-label">Crit</span>
              <span class="roll-mod">{readableDamage(damage.formula.criticalSuccess)}</span>
            </RollButton>
          )}
          <button type="button" class="roll roll-breakdown" onClick={() => { const d = strikeDamageDetail(st, ctx); if (d) onStat(d); }}>Breakdown</button>
        </div>
      ) : (
        <p class="muted">No damage formula.</p>
      )}
      {st.traits.length > 0 && <p class="strike-traits">{st.traits.map((t) => <span key={t} class="tag trait">{t}</span>)}</p>}
      {st.altUsages.map((a) => <StrikeCard key={a.overrideKey} strike={a} ctx={ctx} onStat={onStat} alt />)}
    </section>
  );
}
